/**
 * Módulo de Gestión de Empresas (SaaS)
 * Permite crear, editar, listar y cambiar el estado de las empresas.
 * Cumple con .idx-rules.md: funciones < 40 líneas y archivo < 300 líneas.
 */

import { getAllCompanies, saveCompany } from '../infra/db.js';

let companiesList = [];

/**
 * Carga y renderiza la lista de empresas en el sistema.
 */
export async function loadAndRenderCompanies() {
  try {
    companiesList = await getAllCompanies();
    renderCompaniesTable();
    populateCompanySelect();
  } catch (error) {
    console.error('Error al obtener empresas:', error);
  }
}

/**
 * Obtiene la lista actual de empresas en memoria.
 */
export function getCompaniesList() {
  return companiesList;
}

/**
 * Llena el selector de empresas en el formulario de usuarios.
 */
function populateCompanySelect() {
  const compSelect = document.getElementById('usr-company');
  if (!compSelect) return;
  compSelect.innerHTML = '<option value="">-- Seleccione una Empresa --</option>' + 
    companiesList.map(c => `<option value="${c.id}">${c.name}</option>`).join('');
}

/**
 * Renderiza la tabla de empresas.
 */
function renderCompaniesTable() {
  const tbody = document.getElementById('table-companies-body');
  if (!tbody) return;

  if (companiesList.length === 0) {
    tbody.innerHTML = '<tr><td colspan="4" class="text-center text-muted">No hay empresas registradas.</td></tr>';
    return;
  }

  tbody.innerHTML = companiesList.map(c => {
    const statusText = c.status === 'ACTIVE' ? 'Activo' : 'Inactivo';
    const statusClass = c.status === 'ACTIVE' ? 'badge-gold' : 'badge-red';
    const toggleLabel = c.status === 'ACTIVE' ? 'Baja' : 'Alta';
    const toggleClass = c.status === 'ACTIVE' ? 'btn-danger' : 'btn-primary';

    return `
      <tr>
        <td data-label="ID" class="font-mono text-sm">${c.id}</td>
        <td data-label="Nombre">${c.name}</td>
        <td data-label="Estado"><span class="badge ${statusClass}">${statusText}</span></td>
        <td data-label="Acciones">
          <div style="display: flex; gap: 8px;">
            <button class="btn btn-sm btn-secondary btn-edit-company" data-id="${c.id}">
              <span>✏️</span><span class="btn-text"> Editar</span>
            </button>
            <button class="btn btn-sm ${toggleClass} btn-toggle-company" data-id="${c.id}">
              <span>${c.status === 'ACTIVE' ? '🚫' : '✅'}</span><span class="btn-text"> ${toggleLabel}</span>
            </button>
          </div>
        </td>
      </tr>
    `;
  }).join('');

  setupCompanyTableActions();
}

/**
 * Configura los eventos de los botones en la tabla de empresas.
 */
function setupCompanyTableActions() {
  const tbody = document.getElementById('table-companies-body');
  if (!tbody) return;

  tbody.querySelectorAll('.btn-edit-company').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const id = parseInt(e.currentTarget.getAttribute('data-id'), 10);
      const company = companiesList.find(c => c.id === id);
      if (company) loadCompanyIntoForm(company);
    });
  });

  tbody.querySelectorAll('.btn-toggle-company').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      const id = parseInt(e.currentTarget.getAttribute('data-id'), 10);
      const company = companiesList.find(c => c.id === id);
      if (company) {
        toggleCompanyStatus(company);
      }
    });
  });
}

/**
 * Cambia el estado (Alta/Baja) de una empresa.
 */
async function toggleCompanyStatus(company) {
  try {
    const updated = { ...company, status: company.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE' };
    await saveCompany(updated);
    alert(`Estado de la empresa "${company.name}" modificado.`);
    loadAndRenderCompanies();
  } catch (err) {
    alert(`Error al cambiar estado de empresa: ${err.message}`);
  }
}

/**
 * Carga los datos de la empresa en el formulario de edición.
 */
function loadCompanyIntoForm(company) {
  document.getElementById('company-id').value = company.id;
  document.getElementById('company-name').value = company.name;
  document.getElementById('company-status').value = company.status;
  document.getElementById('company-form-title').innerText = 'Editar Empresa';

  const cancelBtn = document.getElementById('btn-company-cancel');
  if (cancelBtn) cancelBtn.style.display = 'inline-flex';
}

/**
 * Limpia y restablece el formulario de empresas.
 */
export function resetCompanyForm() {
  document.getElementById('companies-form').reset();
  document.getElementById('company-id').value = '';
  document.getElementById('company-form-title').innerText = 'Crear Nueva Empresa';

  const cancelBtn = document.getElementById('btn-company-cancel');
  if (cancelBtn) cancelBtn.style.display = 'none';
}

/**
 * Procesa el envío del formulario de empresas.
 */
async function handleCompanyFormSubmit(e) {
  e.preventDefault();
  const id = document.getElementById('company-id').value;
  const name = document.getElementById('company-name').value.trim();
  const status = document.getElementById('company-status').value;

  try {
    const record = { name, status };
    if (id) record.id = parseInt(id, 10);
    await saveCompany(record);
    alert('Empresa guardada exitosamente.');
    resetCompanyForm();
    loadAndRenderCompanies();
  } catch (error) {
    alert(`Error al guardar empresa: ${error.message}`);
  }
}

/**
 * Inicializa los listeners del módulo de empresas.
 */
export function initCompaniesModule() {
  const compForm = document.getElementById('companies-form');
  compForm?.addEventListener('submit', handleCompanyFormSubmit);

  const compCancelBtn = document.getElementById('btn-company-cancel');
  compCancelBtn?.addEventListener('click', resetCompanyForm);
}
